import * as React from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';

import {
  fetchChannelSummaryByDate,
  fetchClientSummary,
  fetchClientSummaryDateRollup,
  fetchClientSummaryMonthRollup,
  fetchDailyPulseByDMA,
  fetchDailyPulseByState,
  fetchVerticalClientBreakdown,
  fetchVerticalSummary,
} from '@src/api';
import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { formatDateToYMD } from '@src/utils/dateUtils';

import {
  COLUMN_GROUP_SPANS,
  DATE_PRESETS,
  HEATMAP_BG,
  KPI_DEFINITIONS,
  METRIC_COLUMNS,
  detectActivePreset,
  getDeltaTone,
  getHeatmapBand,
  getPreviousPeriod,
} from './companyPerformanceMetrics';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const DAY_RANGE_OPTIONS = [7, 14, 30];

const compactTableSx = {
  '& .MuiTableCell-root': { py: 0.75, px: 1.25, whiteSpace: 'nowrap' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    bgcolor: 'background.neutral',
    py: 1.25,
  },
  // Add breathing room between the header and the first data row.
  '& .MuiTableBody-root .MuiTableRow-root:first-of-type .MuiTableCell-root': {
    pt: 1.25,
  },
};

const stickyControlsSx = {
  position: 'sticky',
  top: 72,
  zIndex: 12,
  borderRadius: 2,
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
  border: '1px solid',
  borderColor: 'divider',
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getTrailingMonthRange = (anchorDate, monthCount = 9) => {
  const safeAnchor = anchorDate instanceof Date && !Number.isNaN(anchorDate.getTime()) ? anchorDate : new Date();
  const end = new Date(safeAnchor.getFullYear(), safeAnchor.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  const start = new Date(end.getFullYear(), end.getMonth() - (monthCount - 1), 1);
  start.setHours(0, 0, 0, 0);
  return { start, end };
};

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

function aggregateBreakdownRows(
  summaryRows = [],
  selectedClientId = '',
  getLabel = () => 'Unspecified',
  labelKey = 'label'
) {
  // Fee-weighted spend (fee_weighted_spend_sum) is computed in SQL per row,
  // so a bucket's fee-weighted total is just the sum of its rows' values.
  const buckets = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const clientId = row.client_id == null ? '' : String(row.client_id);
    if (selectedClientId && clientId !== String(selectedClientId)) {
      return;
    }
    const label = getLabel(row);

    if (!buckets.has(label)) {
      buckets.set(label, {
        id: label,
        [labelKey]: label,
        feeWeightedSpend: 0,
        clientCountSet: new Set(),
        precomputedClientCount: 0,
        total_spend_sum: 0,
        total_leads_sum: 0,
        self_books_sum: 0,
        accepted_sum: 0,
        success_sum: 0,
        out_of_areas_count_sum: 0,
        booked_sum: 0,
        issues_sum: 0,
        scheduled_sum: 0,
        demos_sum: 0,
        gross_sales_sum: 0,
        net_sales_sum: 0,
        net_amount_sum: 0,
      });
    }

    const bucket = buckets.get(label);
    const spend = safeNumber(row.total_spend_sum);
    bucket.precomputedClientCount += safeNumber(row.client_count);
    bucket.total_spend_sum += spend;
    bucket.feeWeightedSpend += safeNumber(row.fee_weighted_spend_sum);
    if (spend > 0 && clientId) {
      bucket.clientCountSet.add(clientId);
    }
    bucket.total_leads_sum += safeNumber(row.total_leads_sum);
    bucket.self_books_sum += safeNumber(row.self_books_sum);
    bucket.accepted_sum += safeNumber(row.accepted_sum);
    bucket.success_sum += safeNumber(row.success_sum);
    bucket.out_of_areas_count_sum += safeNumber(row.out_of_areas_count_sum);
    bucket.booked_sum += safeNumber(row.booked_sum);
    bucket.issues_sum += safeNumber(row.issues_sum);
    bucket.scheduled_sum += safeNumber(row.scheduled_sum);
    bucket.demos_sum += safeNumber(row.demos_sum);
    bucket.gross_sales_sum += safeNumber(row.gross_sales_sum || row.gross_sold_sum);
    bucket.net_sales_sum += safeNumber(row.net_sales_sum || row.net_sold_sum);
    bucket.net_amount_sum += safeNumber(row.net_amount_sum);
  });

  return Array.from(buckets.values())
    .map((row = {}) => ({
      ...row,
      client_count: Math.max(
        row.clientCountSet instanceof Set ? row.clientCountSet.size : 0,
        safeNumber(row.precomputedClientCount)
      ),
    }))
    .sort((left, right) => right.total_spend_sum - left.total_spend_sum);
}

function buildTotals(rows = [], showFee = true) {
  return (rows || []).reduce(
    (acc, row = {}) => {
      const baseSpend = safeNumber(row.total_spend_sum);
      const spend = showFee ? safeNumber(row.feeWeightedSpend) : baseSpend;
      acc.clientCount += safeNumber(row.client_count);
      acc.spend += spend;
      acc.leads += safeNumber(row.total_leads_sum);
      acc.selfBook += safeNumber(row.self_books_sum);
      acc.accepted += safeNumber(row.accepted_sum);
      acc.success += safeNumber(row.success_sum);
      acc.outOfArea += safeNumber(row.out_of_areas_count_sum);
      acc.booked += safeNumber(row.booked_sum);
      acc.issues += safeNumber(row.issues_sum);
      acc.scheduled += safeNumber(row.scheduled_sum);
      acc.demos += safeNumber(row.demos_sum);
      acc.grossSold += safeNumber(row.gross_sales_sum);
      acc.netSold += safeNumber(row.net_sales_sum);
      acc.netAmount += safeNumber(row.net_amount_sum);
      return acc;
    },
    {
      clientCount: 0,
      spend: 0,
      leads: 0,
      selfBook: 0,
      accepted: 0,
      success: 0,
      outOfArea: 0,
      booked: 0,
      issues: 0,
      scheduled: 0,
      demos: 0,
      grossSold: 0,
      netSold: 0,
      netAmount: 0,
    }
  );
}

// Day and month rollups are aggregated server-side (date-rollup / month-rollup
// endpoints), so the page consumes those rows directly instead of bucketing
// per-client-per-day rows in the browser.

const formatAxisCurrency = (value) => {
  const numeric = safeNumber(value);
  if (Math.abs(numeric) >= 1000) {
    return `$${(numeric / 1000).toFixed(0)}k`;
  }
  return `$${numeric.toFixed(0)}`;
};

const chartTooltipFormatter = (value, name) => {
  if (name === 'Spend' || name === 'Spend (incl. fees)') {
    return [new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(value)), name];
  }
  return [safeNumber(value).toLocaleString('en-US'), name];
};

function formatDayLabel(value) {
  if (!value) {
    return 'Unknown';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Format a KPI value for the hero strip. Currency uses k/M abbreviations so
// large totals don't overflow the card; numbers use thousands separators;
// percents render as 0.0% (the format used by PercentageFormatter elsewhere).
function formatKpiValue(value, format) {
  const numeric = Number.isFinite(value) ? value : 0;
  if (format === 'currency') {
    const abs = Math.abs(numeric);
    if (abs >= 1_000_000) {
      return `$${(numeric / 1_000_000).toFixed(2)}M`;
    }
    if (abs >= 10_000) {
      return `$${(numeric / 1_000).toFixed(1)}k`;
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numeric);
  }
  if (format === 'percent') {
    return `${(numeric * 100).toFixed(1)}%`;
  }
  return numeric.toLocaleString('en-US');
}

// Pretty-print a period-over-period delta. Returns null when there's no prior
// value to compare to (so we can hide the delta line entirely).
function computeDelta(current, previous) {
  const a = Number.isFinite(current) ? current : 0;
  const b = Number.isFinite(previous) ? previous : 0;
  if (!b) {
    return null;
  }
  return (a - b) / Math.abs(b);
}

function KpiCard({ definition, current, previous, showFee }) {
  const currentValue = definition.select(current || {});
  const previousValue = definition.select(previous || {});
  const delta = computeDelta(currentValue, previousValue);
  const tone = delta == null ? 'neutral' : getDeltaTone(delta, definition.polarity);
  const toneColor = {
    good: 'success.main',
    bad: 'error.main',
    neutral: 'text.secondary',
  }[tone];
  const DeltaIcon = delta == null ? null : delta >= 0 ? ArrowDropUpIcon : ArrowDropDownIcon;
  const deltaText = delta == null ? '—' : `${Math.abs(delta * 100).toFixed(1)}%`;
  const label = showFee && definition.feeLabel ? definition.feeLabel : definition.label;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        minWidth: 0,
        height: '100%',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2 }}>
        {formatKpiValue(currentValue, definition.format)}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mt: 0.5, color: toneColor }}>
        {DeltaIcon ? <DeltaIcon sx={{ fontSize: 18 }} /> : null}
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {deltaText}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
          vs prev period
        </Typography>
      </Stack>
    </Paper>
  );
}

function KpiHeroStrip({ current, previous, showFee }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(6, 1fr)',
        },
        gap: 1.5,
      }}
    >
      {KPI_DEFINITIONS.map((definition) => (
        <KpiCard
          key={definition.id}
          definition={definition}
          current={current}
          previous={previous}
          showFee={showFee}
        />
      ))}
    </Box>
  );
}

// The month-rollup endpoint returns the first day of each month (e.g.
// "2024-05-01"). Parse the parts directly to avoid UTC/local timezone drift.
function formatMonthLabel(value) {
  if (!value) {
    return 'Unknown';
  }
  const [year, month] = String(value).split('-');
  const yearNum = Number(year);
  const monthNum = Number(month);
  if (!yearNum || !monthNum) {
    return String(value);
  }
  return new Date(yearNum, monthNum - 1, 1).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

function createMetricBucket(id, labelKey, label) {
  return {
    id,
    [labelKey]: label,
    feeWeightedSpend: 0,
    clientCountSet: new Set(),
    total_spend_sum: 0,
    total_leads_sum: 0,
    self_books_sum: 0,
    accepted_sum: 0,
    success_sum: 0,
    out_of_areas_count_sum: 0,
    booked_sum: 0,
    issues_sum: 0,
    scheduled_sum: 0,
    demos_sum: 0,
    gross_sales_sum: 0,
    net_sales_sum: 0,
    net_amount_sum: 0,
  };
}

function applySummaryRowToBucket(bucket, row) {
  const spend = safeNumber(row.total_spend_sum);
  bucket.total_spend_sum += spend;
  bucket.feeWeightedSpend += safeNumber(row.fee_weighted_spend_sum);
  bucket.total_leads_sum += safeNumber(row.total_leads_sum);
  bucket.self_books_sum += safeNumber(row.self_books_sum);
  bucket.accepted_sum += safeNumber(row.accepted_sum);
  bucket.success_sum += safeNumber(row.success_sum);
  bucket.out_of_areas_count_sum += safeNumber(row.out_of_areas_count_sum);
  bucket.booked_sum += safeNumber(row.booked_sum);
  bucket.issues_sum += safeNumber(row.issues_sum);
  bucket.scheduled_sum += safeNumber(row.scheduled_sum);
  bucket.demos_sum += safeNumber(row.demos_sum);
  bucket.gross_sales_sum += safeNumber(row.gross_sales_sum || row.gross_sold_sum);
  bucket.net_sales_sum += safeNumber(row.net_sales_sum || row.net_sold_sum);
  bucket.net_amount_sum += safeNumber(row.net_amount_sum);
}

function finalizeMetricBucket(bucket, children = []) {
  return {
    ...bucket,
    client_count: bucket.clientCountSet instanceof Set ? bucket.clientCountSet.size : safeNumber(bucket.client_count),
    children,
  };
}

function getClientLabel(row = {}, clientLookup = new Map()) {
  const clientId = row.client_id == null ? '' : String(row.client_id);
  return (
    row.client__name ||
    row.client_name ||
    (clientId ? clientLookup.get(clientId) : '') ||
    'Unknown Client'
  );
}

// Two-level hierarchy for the Vertical Performance Table: one row per
// vertical at the top level, each expandable into the clients that
// contributed to it. Input is the (vertical, client) breakdown rows from the
// vertical-client-breakdown endpoint.
function buildVerticalClientHierarchyRows(verticalClientRows = [], selectedClientId = '') {
  const verticalBuckets = new Map();

  (verticalClientRows || []).forEach((row = {}) => {
    const clientId = row.client_id == null ? '' : String(row.client_id);
    if (selectedClientId && clientId !== String(selectedClientId)) {
      return;
    }
    const verticalLabel = (row.vertical || '').trim() || 'Unspecified';
    const clientLabel = row.client__name || row.client_name || 'Unknown Client';
    const verticalId = `vertical:${verticalLabel}`;
    const clientBucketId = `${verticalId}:client:${clientId || clientLabel}`;

    if (!verticalBuckets.has(verticalLabel)) {
      verticalBuckets.set(verticalLabel, {
        bucket: createMetricBucket(verticalId, 'vertical', verticalLabel),
        clients: new Map(),
      });
    }
    const verticalEntry = verticalBuckets.get(verticalLabel);
    applySummaryRowToBucket(verticalEntry.bucket, row);
    if (safeNumber(row.total_spend_sum) > 0 && clientId) {
      verticalEntry.bucket.clientCountSet.add(clientId);
    }

    if (!verticalEntry.clients.has(clientBucketId)) {
      verticalEntry.clients.set(clientBucketId, {
        ...createMetricBucket(clientBucketId, 'client', clientLabel),
        client_count: safeNumber(row.total_spend_sum) > 0 && clientId ? 1 : 0,
      });
    }
    const clientBucket = verticalEntry.clients.get(clientBucketId);
    applySummaryRowToBucket(clientBucket, row);
    if (safeNumber(row.total_spend_sum) > 0 && clientId) {
      clientBucket.clientCountSet.add(clientId);
    }
  });

  return Array.from(verticalBuckets.values())
    .map(({ bucket, clients }) => {
      const clientRows = Array.from(clients.values())
        .map((clientBucket) => finalizeMetricBucket(clientBucket))
        .sort((left, right) => right.total_spend_sum - left.total_spend_sum);
      return finalizeMetricBucket(bucket, clientRows);
    })
    .sort((left, right) => right.total_spend_sum - left.total_spend_sum);
}

function buildGeographicHierarchyRows(summaryRows = [], clientMeta = [], selectedClientId = '', parentLabelGetter = () => 'Unknown', parentKey = 'label') {
  const clientNameById = new Map(
    (clientMeta || []).map((client = {}) => [String(client.value), client.label || client.name || ''])
  );
  const parentBuckets = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const clientId = row.client_id == null ? '' : String(row.client_id);
    if (selectedClientId && clientId !== String(selectedClientId)) {
      return;
    }

    const parentLabel = parentLabelGetter(row);
    const verticalLabel = (row.vertical_label || row.vertical || '').trim() || 'Unspecified';
    const clientLabel = getClientLabel(row, clientNameById);
    const parentId = `${parentKey}:${parentLabel}`;
    const verticalId = `${parentId}:vertical:${verticalLabel}`;
    const clientBucketId = `${verticalId}:client:${clientId || clientLabel}`;

    if (!parentBuckets.has(parentLabel)) {
      parentBuckets.set(parentLabel, {
        bucket: createMetricBucket(parentId, parentKey, parentLabel),
        verticals: new Map(),
      });
    }

    const parentEntry = parentBuckets.get(parentLabel);
    applySummaryRowToBucket(parentEntry.bucket, row);
    if (safeNumber(row.total_spend_sum) > 0 && clientId) {
      parentEntry.bucket.clientCountSet.add(clientId);
    }

    if (!parentEntry.verticals.has(verticalLabel)) {
      parentEntry.verticals.set(verticalLabel, {
        bucket: createMetricBucket(verticalId, 'vertical', verticalLabel),
        clients: new Map(),
      });
    }

    const verticalEntry = parentEntry.verticals.get(verticalLabel);
    applySummaryRowToBucket(verticalEntry.bucket, row);
    if (safeNumber(row.total_spend_sum) > 0 && clientId) {
      verticalEntry.bucket.clientCountSet.add(clientId);
    }

    if (!verticalEntry.clients.has(clientBucketId)) {
      verticalEntry.clients.set(clientBucketId, {
        ...createMetricBucket(clientBucketId, 'client', clientLabel),
        client_count: safeNumber(row.total_spend_sum) > 0 && clientId ? 1 : 0,
      });
    }

    const clientBucket = verticalEntry.clients.get(clientBucketId);
    applySummaryRowToBucket(clientBucket, row);
    if (safeNumber(row.total_spend_sum) > 0 && clientId) {
      clientBucket.clientCountSet.add(clientId);
    }
  });

  return Array.from(parentBuckets.values())
    .map(({ bucket, verticals }) => {
      const verticalRows = Array.from(verticals.values())
        .map(({ bucket: verticalBucket, clients }) => {
          const clientRows = Array.from(clients.values())
            .map((clientBucket) => finalizeMetricBucket(clientBucket))
            .sort((left, right) => right.total_spend_sum - left.total_spend_sum);
          return finalizeMetricBucket(verticalBucket, clientRows);
        })
        .sort((left, right) => right.total_spend_sum - left.total_spend_sum);

      return finalizeMetricBucket(bucket, verticalRows);
    })
    .sort((left, right) => right.total_spend_sum - left.total_spend_sum);
}

function getDisplayMetrics(row = {}, showFee = true) {
  const baseSpend = safeNumber(row.total_spend_sum);
  const spend = showFee ? safeNumber(row.feeWeightedSpend) : baseSpend;
  const leads = safeNumber(row.total_leads_sum);
  const selfBook = safeNumber(row.self_books_sum);
  const accepted = safeNumber(row.accepted_sum);
  const success = safeNumber(row.success_sum);
  const outOfArea = safeNumber(row.out_of_areas_count_sum);
  const booked = safeNumber(row.booked_sum);
  const issues = safeNumber(row.issues_sum);
  const scheduled = safeNumber(row.scheduled_sum);
  const demos = safeNumber(row.demos_sum);
  const grossSold = safeNumber(row.gross_sales_sum);
  const netSold = safeNumber(row.net_sales_sum);
  const netAmount = safeNumber(row.net_amount_sum);

  return {
    spend,
    leads,
    selfBook,
    accepted,
    success,
    outOfArea,
    booked,
    issues,
    scheduled,
    demos,
    grossSold,
    netSold,
    netAmount,
  };
}

// Render one metric value with appropriate format and an optional heatmap
// background tint when the column declares healthy thresholds.
function MetricCell({ value, format, heatmap, boldText = false }) {
  const band = heatmap ? getHeatmapBand(value, heatmap) : null;
  const cellSx = {
    ...(band ? { backgroundColor: HEATMAP_BG[band] } : null),
    ...(boldText ? { fontWeight: 700 } : null),
  };

  let content;
  if (format === 'currency') {
    content = <DollarFormatter value={value} />;
  } else if (format === 'percent') {
    content = <PercentageFormatter value={value} />;
  } else {
    const numeric = Number.isFinite(value) ? value : 0;
    content = numeric.toLocaleString('en-US');
  }

  return <TableCell sx={cellSx}>{content}</TableCell>;
}

function SummaryMetricsCells({ row, showFee, showClientCount, label, hasChildren = false, level = 0, expanded = false, onToggle }) {
  const metrics = getDisplayMetrics(row, showFee);
  const indent = level * 2.5;

  return (
    <>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', pl: indent }}>
          {hasChildren ? (
            <IconButton size="small" onClick={onToggle} sx={{ mr: 0.5 }}>
              {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 32, flexShrink: 0 }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: level === 0 ? 700 : 400 }}>
            {label}
          </Typography>
        </Box>
      </TableCell>
      {showClientCount ? <TableCell>{safeNumber(row.client_count)}</TableCell> : null}
      {METRIC_COLUMNS.map((column) => (
        <MetricCell
          key={column.id}
          value={column.select(metrics)}
          format={column.format}
          heatmap={column.heatmap}
        />
      ))}
    </>
  );
}

// Header is two rows: top row groups columns under Volume / Lead Quality / …,
// bottom row carries the individual labels (with hover tooltips). The label
// and Clients columns use rowSpan=2 so they cross both header rows.
function PerformanceTableHead({ labelHeader, showFee, showClientCount }) {
  return (
    <TableHead>
      <TableRow>
        <TableCell rowSpan={2} sx={stickyHeaderRowSpanSx}>{labelHeader}</TableCell>
        {showClientCount ? (
          <TableCell rowSpan={2} sx={stickyHeaderRowSpanSx}>Clients</TableCell>
        ) : null}
        {COLUMN_GROUP_SPANS.map((group) => (
          <TableCell
            key={group.id}
            colSpan={group.span}
            align="center"
            sx={{
              ...stickyHeaderTopSx,
              borderLeft: '1px solid',
              borderColor: 'divider',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
            }}
          >
            {group.label}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {METRIC_COLUMNS.map((column) => {
          const label = showFee && column.feeLabel ? column.feeLabel : column.label;
          return (
            <TableCell key={column.id} sx={stickyHeaderBottomSx}>
              <Tooltip title={column.tooltip || ''} arrow placement="top">
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                  <span>{label}</span>
                  <HelpOutlineIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                </Box>
              </Tooltip>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

// The summed "Total" row at the bottom of each table. `totals` shape matches
// getDisplayMetrics output, so column selectors work on it directly.
function PerformanceTotalsRow({ totals, showClientCount }) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
      {showClientCount ? (
        <TableCell sx={{ fontWeight: 700 }}>{totals.clientCount}</TableCell>
      ) : null}
      {METRIC_COLUMNS.map((column) => (
        <MetricCell
          key={column.id}
          value={column.select(totals)}
          format={column.format}
          heatmap={column.heatmap}
          boldText
        />
      ))}
    </TableRow>
  );
}

// Column count for empty-state colSpan: label + (clients?) + every metric column.
function getTotalColumnCount(showClientCount) {
  return 1 + (showClientCount ? 1 : 0) + METRIC_COLUMNS.length;
}

const stickyHeaderTopSx = {
  position: 'sticky',
  top: 0,
  zIndex: 3,
  backgroundColor: 'background.paper',
};

// Cells that rowSpan across both header rows (Day / Month / Vertical / Channel
// / Clients). Aligning the text to the bottom puts them flush with the lower
// column-labels row instead of floating mid-header.
const stickyHeaderRowSpanSx = {
  ...stickyHeaderTopSx,
  verticalAlign: 'bottom',
  pb: 1.25,
};

const stickyHeaderBottomSx = {
  position: 'sticky',
  top: 36,
  zIndex: 2,
  backgroundColor: 'background.paper',
  borderBottom: '2px solid',
  borderBottomColor: 'divider',
};

function PerformanceSummaryTable({
  title,
  subheader,
  labelHeader,
  labelKey,
  rows,
  totals,
  showFee,
  emptyMessage,
  showClientCount = false,
  loading = false,
  action,
  footer,
}) {
  return (
    <Card>
      <CardHeader title={title} subheader={subheader} action={action} />
      <CardContent>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', maxHeight: 640 }}>
          <Table size="small" sx={compactTableSx}>
            <PerformanceTableHead
              labelHeader={labelHeader}
              showFee={showFee}
              showClientCount={showClientCount}
            />
            <TableBody>
              {rows.length ? (
                <>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <SummaryMetricsCells
                        row={row}
                        showFee={showFee}
                        showClientCount={showClientCount}
                        label={row[labelKey]}
                      />
                    </TableRow>
                  ))}
                  <PerformanceTotalsRow totals={totals} showClientCount={showClientCount} />
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={getTotalColumnCount(showClientCount)} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? 'Loading…' : emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {footer}
      </CardContent>
    </Card>
  );
}

// Per-client performance with a name search and paginated rows. Filtering and
// totals run client-side over the cached `client` rollup; for a few hundred
// clients that's well under a millisecond per keystroke.
const CLIENT_PERF_PAGE_SIZE = 25;

function ClientPerformanceCard({ rows, showFee, loading }) {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      String(row.client || row.client__name || row.client_name || '')
        .toLowerCase()
        .includes(term)
    );
  }, [rows, search]);

  // Reset to page 1 whenever the filtered set changes (new search term or
  // new fetch). Avoids landing on an empty page after a narrowing search.
  React.useEffect(() => {
    setPage(1);
  }, [search, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / CLIENT_PERF_PAGE_SIZE));
  const pagedRows = React.useMemo(
    () =>
      filteredRows.slice(
        (page - 1) * CLIENT_PERF_PAGE_SIZE,
        page * CLIENT_PERF_PAGE_SIZE
      ),
    [filteredRows, page]
  );
  // Totals reflect the active filter, not just the current page.
  const totals = React.useMemo(
    () => buildTotals(filteredRows, showFee),
    [filteredRows, showFee]
  );

  const subheader = search
    ? `${filteredRows.length} of ${rows.length} clients match “${search}”`
    : `${rows.length} client${rows.length === 1 ? '' : 's'} in range`;

  return (
    <PerformanceSummaryTable
      title="Client Performance Table"
      subheader={subheader}
      labelHeader="Client"
      labelKey="client"
      rows={pagedRows}
      totals={totals}
      showFee={showFee}
      emptyMessage={
        search
          ? 'No clients match your search.'
          : 'No client summary rows found for the selected range.'
      }
      loading={loading}
      action={
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search client name"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
      }
      footer={
        totalPages > 1 ? (
          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_event, next) => setPage(next)}
              color="primary"
              shape="rounded"
            />
          </Stack>
        ) : null
      }
    />
  );
}

function ExpandablePerformanceSummaryTable({
  title,
  subheader,
  labelHeader,
  labelKey,
  rows,
  totals,
  showFee,
  emptyMessage,
  showClientCount = false,
  loading = false,
}) {
  const [expandedRows, setExpandedRows] = React.useState({});

  React.useEffect(() => {
    setExpandedRows({});
  }, [rows]);

  const toggleRow = React.useCallback((rowId) => {
    setExpandedRows((current) => ({
      ...current,
      [rowId]: !current[rowId],
    }));
  }, []);

  const renderRows = React.useCallback((items = [], level = 0) => (
    items.flatMap((row) => {
      const hasChildren = Array.isArray(row.children) && row.children.length > 0;
      const expanded = Boolean(expandedRows[row.id]);
      const label = row[labelKey] || row.vertical || row.client || row.state || row.dma || 'Unknown';
      const rendered = [
        <TableRow key={row.id}>
          <SummaryMetricsCells
            row={row}
            showFee={showFee}
            showClientCount={showClientCount}
            label={label}
            hasChildren={hasChildren}
            level={level}
            expanded={expanded}
            onToggle={hasChildren ? () => toggleRow(row.id) : undefined}
          />
        </TableRow>,
      ];

      if (hasChildren) {
        rendered.push(
          <TableRow key={`${row.id}:children`}>
            <TableCell sx={{ p: 0, borderBottom: expanded ? 0 : undefined }} colSpan={getTotalColumnCount(showClientCount)}>
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Table size="small" sx={compactTableSx}>
                  <TableBody>{renderRows(row.children, level + 1)}</TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        );
      }

      return rendered;
    })
  ), [expandedRows, labelKey, showClientCount, showFee, toggleRow]);

  return (
    <Card>
      <CardHeader title={title} subheader={subheader} />
      <CardContent>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', maxHeight: 640 }}>
          <Table size="small" sx={compactTableSx}>
            <PerformanceTableHead
              labelHeader={labelHeader}
              showFee={showFee}
              showClientCount={showClientCount}
            />
            <TableBody>
              {rows.length ? (
                <>
                  {renderRows(rows)}
                  <PerformanceTotalsRow totals={totals} showClientCount={showClientCount} />
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={getTotalColumnCount(showClientCount)} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? 'Loading…' : emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default function CompanyPerformance() {
  const defaultRange = React.useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = React.useState(defaultRange.start);
  const [endDate, setEndDate] = React.useState(defaultRange.end);
  const [showFee, setShowFee] = React.useState(true);
  const [backendOnly, setBackendOnly] = React.useState(false);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [selectedVertical, setSelectedVertical] = React.useState('');
  const [dayRange, setDayRange] = React.useState(DAY_RANGE_OPTIONS[1]);
  const [summaryRows, setSummaryRows] = React.useState([]);
  const [clientRowsRaw, setClientRowsRaw] = React.useState([]);
  const [verticalClientRowsRaw, setVerticalClientRowsRaw] = React.useState([]);
  const [channelRowsRaw, setChannelRowsRaw] = React.useState([]);
  const [stateRowsRaw, setStateRowsRaw] = React.useState([]);
  const [dmaRowsRaw, setDmaRowsRaw] = React.useState([]);
  const [timeSeriesRows, setTimeSeriesRows] = React.useState([]);
  const [monthSeriesRows, setMonthSeriesRows] = React.useState([]);
  // Previous-period rollup rows that feed the KPI deltas. Same shape as
  // `timeSeriesRows`, just spanning the period immediately preceding the
  // active one (length-matched).
  const [prevTimeSeriesRows, setPrevTimeSeriesRows] = React.useState([]);
  const [verticalOptions, setVerticalOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [geoLoading, setGeoLoading] = React.useState(false);
  const [geoExpanded, setGeoExpanded] = React.useState(false);
  const [geoLoaded, setGeoLoaded] = React.useState(false);
  const [error, setError] = React.useState(null);
  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientInputChange,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();
  const clients = React.useMemo(() => clientOptions, [clientOptions]);

  React.useEffect(() => {
    let active = true;

    const loadPrimaryData = async () => {
      setLoading(true);
      setError(null);
      try {
        const basePayload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
          ...(backendOnly ? { backend_only: true } : {}),
        };
        const trailingMonthRange = getTrailingMonthRange(endDate, 9);
        const monthPayload = {
          date_after: formatDateToYMD(trailingMonthRange.start),
          date_before: formatDateToYMD(trailingMonthRange.end),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
          ...(backendOnly ? { backend_only: true } : {}),
        };
        // Length-matched preceding period for KPI deltas. Tiny payload via
        // the rollup endpoint, so an extra parallel call is cheap.
        const previousPeriod = getPreviousPeriod(startDate, endDate);
        const prevPayload = previousPeriod
          ? {
              date_after: formatDateToYMD(previousPeriod.start),
              date_before: formatDateToYMD(previousPeriod.end),
              ...(selectedClientId ? { client_id: selectedClientId } : {}),
              ...(backendOnly ? { backend_only: true } : {}),
            }
          : null;
        const [
          summaryResponse,
          channelResponse,
          clientResponse,
          verticalClientResponse,
          timeSeriesResponse,
          monthSeriesResponse,
          prevTimeSeriesResponse,
        ] = await Promise.all([
          fetchVerticalSummary(basePayload),
          fetchChannelSummaryByDate(basePayload),
          fetchClientSummary(basePayload),
          fetchVerticalClientBreakdown(basePayload),
          fetchClientSummaryDateRollup(basePayload),
          fetchClientSummaryMonthRollup(monthPayload),
          prevPayload ? fetchClientSummaryDateRollup(prevPayload) : Promise.resolve({ data: [] }),
        ]);

        if (!active) {
          return;
        }

        const verticalOptionRows = Array.isArray(summaryResponse?.data) ? summaryResponse.data : [];
        setVerticalOptions(
          Array.from(
            new Set(
              verticalOptionRows
                .map((row = {}) => (row.vertical || '').trim())
                .filter(Boolean)
            )
          ).sort((a, b) => a.localeCompare(b))
        );
        setSummaryRows(verticalOptionRows);
        setClientRowsRaw(Array.isArray(clientResponse?.data) ? clientResponse.data : []);
        setVerticalClientRowsRaw(
          Array.isArray(verticalClientResponse?.data) ? verticalClientResponse.data : []
        );
        setChannelRowsRaw(Array.isArray(channelResponse?.data) ? channelResponse.data : []);
        setTimeSeriesRows(Array.isArray(timeSeriesResponse?.data) ? timeSeriesResponse.data : []);
        setMonthSeriesRows(Array.isArray(monthSeriesResponse?.data) ? monthSeriesResponse.data : []);
        setPrevTimeSeriesRows(Array.isArray(prevTimeSeriesResponse?.data) ? prevTimeSeriesResponse.data : []);
      } catch (err) {
        console.error('Failed to load company performance data', err);
        if (active) {
          setError('Failed to load company performance data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPrimaryData();

    return () => {
      active = false;
    };
  }, [startDate, endDate, selectedClientId, backendOnly]);

  React.useEffect(() => {
    if (!geoExpanded) return;
    let active = true;

    const loadGeographicData = async () => {
      setGeoLoading(true);
      try {
        const basePayload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
          ...(backendOnly ? { backend_only: true } : {}),
        };
        const breakdownPayload = {
          ...basePayload,
          ...(selectedVertical ? { vertical: selectedVertical } : {}),
        };
        const [stateResponse, dmaResponse] = await Promise.all([
          fetchDailyPulseByState(breakdownPayload),
          fetchDailyPulseByDMA(breakdownPayload),
        ]);

        if (!active) {
          return;
        }

        setStateRowsRaw(Array.isArray(stateResponse?.data) ? stateResponse.data : []);
        setDmaRowsRaw(Array.isArray(dmaResponse?.data) ? dmaResponse.data : []);
        setGeoLoaded(true);
      } catch (err) {
        console.error('Failed to load geographic performance data', err);
        if (active) {
          setStateRowsRaw([]);
          setDmaRowsRaw([]);
        }
      } finally {
        if (active) {
          setGeoLoading(false);
        }
      }
    };

    loadGeographicData();

    return () => {
      active = false;
    };
  }, [geoExpanded, startDate, endDate, selectedClientId, selectedVertical, backendOnly]);

  const allVerticalRows = React.useMemo(
    () => aggregateBreakdownRows(
      summaryRows,
      '',
      (row = {}) => (row.vertical || '').trim() || 'Unspecified',
      'vertical'
    ),
    [summaryRows]
  );

  const rows = React.useMemo(
    () => (selectedVertical ? allVerticalRows.filter((row = {}) => row.vertical === selectedVertical) : allVerticalRows),
    [allVerticalRows, selectedVertical]
  );
  // Vertical → clients hierarchy rows for the expandable Vertical Performance
  // Table. Top-level totals come from the (vertical, client) breakdown rows
  // so they line up with the children when expanded.
  const verticalHierarchyRows = React.useMemo(() => {
    const built = buildVerticalClientHierarchyRows(verticalClientRowsRaw, selectedClientId);
    if (!selectedVertical) return built;
    return built.filter((row = {}) => row.vertical === selectedVertical);
  }, [verticalClientRowsRaw, selectedClientId, selectedVertical]);
  // Per-client rows for the Client Performance Table. The `client` endpoint
  // already returns one fully-aggregated row per client (with
  // fee_weighted_spend_sum); just normalize the shape `ClientPerformanceCard`
  // expects and sort by spend desc.
  const clientPerfRows = React.useMemo(
    () =>
      (clientRowsRaw || [])
        .map((row = {}) => ({
          ...row,
          id: String(row.client_id || row.uuid || row.client__name || ''),
          client: row.client__name || row.client_name || 'Unknown Client',
          feeWeightedSpend: safeNumber(row.fee_weighted_spend_sum),
        }))
        .sort(
          (left, right) =>
            safeNumber(right.total_spend_sum) - safeNumber(left.total_spend_sum)
        ),
    [clientRowsRaw]
  );
  const channelRows = React.useMemo(
    () => aggregateBreakdownRows(
      channelRowsRaw,
      selectedClientId,
      (row = {}) => (row.channel__name || row.channel || row.channel_name || '').trim() || 'Unspecified',
      'channel'
    ),
    [channelRowsRaw, selectedClientId]
  );
  const stateRows = React.useMemo(
    () => aggregateBreakdownRows(
      stateRowsRaw,
      selectedClientId,
      (row = {}) =>
        (row.displayName || row.state || row.state__name || row.state_name || '').trim() || 'Unknown State',
      'state'
    ),
    [selectedClientId, stateRowsRaw]
  );
  const dmaRows = React.useMemo(
    () => aggregateBreakdownRows(
      dmaRowsRaw,
      selectedClientId,
      (row = {}) =>
        (
          row.displayName ||
          row.dma_vertical_label ||
          row.dma__name ||
          row.dma ||
          row.dma_name ||
          ''
        ).trim() || 'Unknown DMA',
      'dma'
    ),
    [dmaRowsRaw, selectedClientId]
  );
  const stateHierarchyRows = React.useMemo(
    () => buildGeographicHierarchyRows(
      stateRowsRaw,
      clients,
      selectedClientId,
      (row = {}) =>
        (row.displayName || row.state || row.state__name || row.state_name || '').trim() || 'Unknown State',
      'state'
    ),
    [clients, selectedClientId, stateRowsRaw]
  );
  const dmaHierarchyRows = React.useMemo(
    () => buildGeographicHierarchyRows(
      dmaRowsRaw,
      clients,
      selectedClientId,
      (row = {}) =>
        (
          row.displayName ||
          row.dma_vertical_label ||
          row.dma__name ||
          row.dma_name ||
          row.dma ||
          ''
        ).trim() || 'Unknown DMA',
      'dma'
    ),
    [clients, dmaRowsRaw, selectedClientId]
  );
  // date-rollup returns one fully-aggregated row per day already; the page
  // just normalizes the fee-weighted spend key and sorts ascending.
  const summaryByDay = React.useMemo(() => {
    // Drop future-dated rows: some summary jobs pre-create empty rows for
    // upcoming days, which would otherwise show as zero-spend days in the
    // chart and DoD table.
    const todayYMD = formatDateToYMD(new Date());
    return (timeSeriesRows || [])
      .filter((row = {}) => row.date && String(row.date) <= todayYMD)
      .map((row = {}) => ({
        ...row,
        feeWeightedSpend: safeNumber(row.fee_weighted_spend_sum),
        spendWithFees: safeNumber(row.fee_weighted_spend_sum),
      }))
      .sort((left, right) => String(left.date).localeCompare(String(right.date)));
  }, [timeSeriesRows]);
  // month-rollup returns one fully-aggregated row per month, newest first.
  const monthRows = React.useMemo(
    () =>
      (monthSeriesRows || [])
        .map((row = {}) => ({
          ...row,
          id: String(row.month),
          month: formatMonthLabel(row.month),
          feeWeightedSpend: safeNumber(row.fee_weighted_spend_sum),
        }))
        .slice(0, 9),
    [monthSeriesRows]
  );
  const dodRows = React.useMemo(() => {
    const sorted = [...summaryByDay].sort((left, right) =>
      String(right.date).localeCompare(String(left.date))
    );
    return sorted.slice(0, dayRange).map((row = {}) => ({
      ...row,
      id: row.date,
      day: formatDayLabel(row.date),
    }));
  }, [dayRange, summaryByDay]);

  const totals = React.useMemo(() => buildTotals(rows, showFee), [rows, showFee]);
  const channelTotals = React.useMemo(() => buildTotals(channelRows, showFee), [channelRows, showFee]);
  const monthTotals = React.useMemo(() => buildTotals(monthRows, showFee), [monthRows, showFee]);
  const dodTotals = React.useMemo(() => buildTotals(dodRows, showFee), [dodRows, showFee]);
  const stateTotals = React.useMemo(() => buildTotals(stateRows, showFee), [stateRows, showFee]);
  const dmaTotals = React.useMemo(() => buildTotals(dmaRows, showFee), [dmaRows, showFee]);

  // KPI hero strip totals — period grand totals + previous-period equivalent
  // for delta arrows. Both built from the date-rollup rows.
  const kpiCurrent = React.useMemo(() => buildTotals(summaryByDay, showFee), [summaryByDay, showFee]);
  const kpiPrevious = React.useMemo(
    () => buildTotals(
      (prevTimeSeriesRows || []).map((row = {}) => ({
        ...row,
        feeWeightedSpend: safeNumber(row.fee_weighted_spend_sum),
      })),
      showFee
    ),
    [prevTimeSeriesRows, showFee]
  );

  const handleDateRangeChange = React.useCallback((ranges) => {
    const nextStart = ranges?.selection?.startDate;
    const nextEnd = ranges?.selection?.endDate;
    if (nextStart instanceof Date && nextEnd instanceof Date) {
      setStartDate(nextStart);
      setEndDate(nextEnd);
    }
  }, []);

  // Highlights the active preset chip when the current dates exactly match
  // a preset's range, otherwise null (no chip highlighted → custom range).
  const activePresetId = React.useMemo(
    () => detectActivePreset(startDate, endDate),
    [startDate, endDate]
  );

  const handlePresetClick = React.useCallback((_event, presetId) => {
    if (!presetId) {
      return;
    }
    const preset = DATE_PRESETS.find((p) => p.id === presetId);
    if (!preset) {
      return;
    }
    const range = preset.getRange();
    setStartDate(range.start);
    setEndDate(range.end);
  }, []);

  return (
    <Page title="Company Performance" fullWidth isLoading={loading}>
      <Stack spacing={3}>
        <Typography variant="body2" color="text.secondary">
          Staff-only vertical summary rollup using the same metrics as the client dashboard vertical table. KPI deltas compare to the same-length period immediately before.
        </Typography>

        <KpiHeroStrip
          current={kpiCurrent}
          previous={kpiPrevious}
          showFee={showFee}
        />

        <Paper
          sx={{
            ...stickyControlsSx,
            backgroundColor: 'background.paper',
            p: { xs: 1.25, md: 1.5 },
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <ToggleButtonGroup
                value={activePresetId}
                exclusive
                size="small"
                onChange={handlePresetClick}
                sx={{
                  flexWrap: 'wrap',
                  '& .MuiToggleButton-root': {
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 1.5,
                    py: 0.4,
                    fontWeight: 600,
                    fontSize: 12,
                    textTransform: 'none',
                  },
                }}
              >
                {DATE_PRESETS.map((preset) => (
                  <ToggleButton key={preset.id} value={preset.id}>
                    {preset.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                {activePresetId
                  ? `${DATE_PRESETS.find((p) => p.id === activePresetId)?.label} selected`
                  : 'Custom range'}
              </Typography>
            </Stack>
            <Divider flexItem />
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
                  <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onChange={handleDateRangeChange}
                    style={{ width: '100%' }}
                  />
                </Box>
              <Autocomplete
                sx={{ minWidth: 260 }}
                options={clientOptions}
                loading={clientsLoading}
                value={findClientOptionByValue(selectedClientId)}
                onInputChange={handleClientInputChange}
                onChange={(_event, option) => setSelectedClientId(option ? String(option.value) : '')}
                isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="All clients"
                  />
                )}
              />
              <Autocomplete
                sx={{ minWidth: 260 }}
                options={verticalOptions}
                value={selectedVertical || null}
                onChange={(_event, option) => setSelectedVertical(option || '')}
                getOptionLabel={(option) => option || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vertical Filter"
                    placeholder="All verticals"
                  />
                )}
              />
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <FormControlLabel
                control={<Switch checked={showFee} onChange={(event) => setShowFee(event.target.checked)} />}
                label="Include fees"
              />
              <ToggleButtonGroup
                value={backendOnly ? 'backend_only' : 'all'}
                exclusive
                size="small"
                onChange={(_, v) => { if (v) setBackendOnly(v === 'backend_only'); }}
                sx={{ '& .MuiToggleButton-root': { border: 0, px: 1.4, py: 0.5, borderRadius: 2, fontWeight: 700, fontSize: 13 } }}
              >
                <ToggleButton value="all">All Clients</ToggleButton>
                <ToggleButton value="backend_only">Backend Only</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card>
          <CardHeader
            title="Performance Over Time"
            subheader="Spend, leads, booked, issues and demos by day across all matching clients."
          />
          <CardContent>
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summaryByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" tickFormatter={formatAxisCurrency} />
                  <YAxis yAxisId="right" orientation="right" />
                  <RechartsTooltip formatter={chartTooltipFormatter} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey={showFee ? 'spendWithFees' : 'total_spend_sum'}
                    name={showFee ? 'Spend (incl. fees)' : 'Spend'}
                    stroke="#8884d8"
                    yAxisId="left"
                  />
                  <Line type="monotone" dataKey="total_leads_sum" name="Leads" stroke="#82ca9d" yAxisId="right" />
                  <Line type="monotone" dataKey="booked_sum" name="Booked" stroke="#ffc658" yAxisId="right" />
                  <Line type="monotone" dataKey="issues_sum" name="Issues" stroke="#ff7300" yAxisId="right" />
                  <Line type="monotone" dataKey="demos_sum" name="Demos" stroke="#413ea0" yAxisId="right" />
                  <Line type="monotone" dataKey="net_sales_sum" name="Net Sold" stroke="#00C49F" yAxisId="right" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        <PerformanceSummaryTable
          title="MoM Performance Table"
          subheader={`Last ${monthRows.length} month${monthRows.length === 1 ? '' : 's'} of data`}
          labelHeader="Month"
          labelKey="month"
          rows={monthRows}
          totals={monthTotals}
          showFee={showFee}
          emptyMessage="No month-over-month rows found for the last 9 months."
        />

        <PerformanceSummaryTable
          title="DoD Performance"
          subheader={`${dodRows.length} day${dodRows.length === 1 ? '' : 's'} shown — daily performance window similar to the client dashboard.`}
          labelHeader="Day"
          labelKey="day"
          rows={dodRows}
          totals={dodTotals}
          showFee={showFee}
          emptyMessage="No day-over-day rows found for the selected range."
          action={(
            <ToggleButtonGroup
              color="primary"
              value={dayRange}
              exclusive
              size="small"
              onChange={(_event, nextValue) => {
                if (nextValue) {
                  setDayRange(nextValue);
                }
              }}
            >
              {DAY_RANGE_OPTIONS.map((option) => (
                <ToggleButton key={option} value={option}>
                  {option}d
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        />

        <ExpandablePerformanceSummaryTable
          title="Vertical Performance Table"
          subheader={`${verticalHierarchyRows.length} vertical${verticalHierarchyRows.length === 1 ? '' : 's'} in range — expand a row to see the clients in that vertical`}
          labelHeader="Vertical"
          labelKey="vertical"
          rows={verticalHierarchyRows}
          totals={totals}
          showFee={showFee}
          emptyMessage="No vertical summary rows found for the selected range."
        />

        <ClientPerformanceCard
          rows={clientPerfRows}
          showFee={showFee}
          loading={loading}
        />

        <PerformanceSummaryTable
          title="Channel Performance Table"
          subheader={`${channelRows.length} channel${channelRows.length === 1 ? '' : 's'} in range`}
          labelHeader="Channel"
          labelKey="channel"
          rows={channelRows}
          totals={channelTotals}
          showFee={showFee}
          emptyMessage="No channel summary rows found for the selected range."
        />

        <Card>
          <CardHeader
            title="Geographic Breakdown"
            subheader={geoExpanded ? 'State and DMA performance tables' : 'Click to load state and DMA breakdowns'}
            action={
              <IconButton onClick={() => setGeoExpanded((prev) => !prev)}>
                {geoExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
            }
            sx={{ cursor: 'pointer' }}
            onClick={() => setGeoExpanded((prev) => !prev)}
          />
        </Card>

        <Collapse in={geoExpanded} unmountOnExit>
          <Stack spacing={3}>
            <ExpandablePerformanceSummaryTable
              title="State Performance Table"
              subheader={`${stateRows.length} state${stateRows.length === 1 ? '' : 's'} in range with vertical and client drilldown`}
              labelHeader="State"
              labelKey="state"
              rows={stateHierarchyRows}
              totals={stateTotals}
              showFee={showFee}
              emptyMessage="No state performance rows found for the selected range."
              showClientCount
              loading={geoLoading}
            />

            <ExpandablePerformanceSummaryTable
              title="DMA Performance Table"
              subheader={`${dmaRows.length} DMA${dmaRows.length === 1 ? '' : 's'} in range with vertical and client drilldown`}
              labelHeader="DMA"
              labelKey="dma"
              rows={dmaHierarchyRows}
              totals={dmaTotals}
              showFee={showFee}
              emptyMessage="No DMA performance rows found for the selected range."
              showClientCount
              loading={geoLoading}
            />
          </Stack>
        </Collapse>
      </Stack>
    </Page>
  );
}
